import { UPGRADE_OFFER_LABEL } from '@pubstack/common/src/upgrade'
import { IconName } from '~/components/Icon'
import { UPGRADE_OFFER_BENEFITS, UPGRADE_OFFER_PITCH } from './upgradeOffer'

/**
 * Everything Pubstack sells, as a customer hears it pitched.
 *
 * Deliberately **not** keyed by `UpgradeOffer`. That union is a gate: it answers "does this scope own the
 * product behind this tab", every entry needs a `Feature`, and `scopeOwnsOffer` branches on it by hand.
 * Only One and Node gate an ad unit tab, so only they belong there — widening the union to get the two
 * Spark products onto this page would hand them Node's entitlement through that function's else branch,
 * and flip `scopeOwnsNoOffer` for a Spark-only scope, which would silently change what a different page
 * shows. A pitch is not an entitlement, so it gets its own list and nothing here decides access.
 *
 * One and Node still read their copy from `upgradeOffer.ts` rather than restating it, so a product cannot
 * make one claim on this page and a different one on the tab it gates.
 *
 * Names are the ones on pubstack.io, not the ones in the code: a seat is typed `spark_for_pubs`, but the
 * product is "Spark for Publishers" and no customer should ever be shown the abbreviation.
 */

/** The two questions a publisher is actually asking, which is how the four products divide up. */
export type ProductFamily = 'stack' | 'demand'

export const PRODUCT_FAMILIES: { id: ProductFamily; title: string; subtitle: string }[] = [
  { id: 'stack', title: 'Run your stack', subtitle: 'Every ad unit you own, served from one place — without waiting on a release.' },
  { id: 'demand', title: 'Sell it for what it is worth', subtitle: 'Stop letting your inventory be traded as a commodity it is not.' },
]

export type ProductPitch = {
  name: string
  /** The line to keep if the reader keeps only one. */
  tagline: string
  description: string
  /** Outcomes the customer walks away with, never a list of what we built. */
  benefits: string[]
  icon: IconName
  family: ProductFamily
}

export const PRODUCT_PITCHES: ProductPitch[] = [
  {
    name: UPGRADE_OFFER_LABEL.one,
    tagline: 'Your whole ad stack, under your own control.',
    description: UPGRADE_OFFER_PITCH.one,
    benefits: UPGRADE_OFFER_BENEFITS.one,
    icon: 'ad_stack',
    family: 'stack',
  },
  {
    name: UPGRADE_OFFER_LABEL.node,
    tagline: 'Server-side, made simple.',
    description: UPGRADE_OFFER_PITCH.node,
    benefits: UPGRADE_OFFER_BENEFITS.node,
    icon: 'server',
    family: 'stack',
  },
  {
    name: 'Spark Demand',
    tagline: 'Your inventory, valued rather than commoditised.',
    description:
      'Pubstack enriches each impression with real-time signals — engagement, ad pressure, screen exclusivity — and connects it to premium demand we bring to the table. Curated deals, run for you, on top of everything you already sell.',
    benefits: [
      'Prove what your inventory is worth with impression-level signals.',
      'Reach brand budgets through deals Pubstack curates and operates.',
      'Win more of the impressions you already send, at a higher price.',
    ],
    icon: 'store_front',
    family: 'demand',
  },
  {
    name: 'Spark for Publishers',
    tagline: 'The Spark engine, in your hands.',
    description:
      'The same signal engine, driven from your own console and your own SSP seats. You decide how your inventory is packaged, which impressions go to market, and what a buyer has to see to bid on it.',
    benefits: [
      'Package your inventory into signal-based pools buyers recognise.',
      'Shape traffic so only high-value impressions reach the market.',
      'Turn your first-party data into curated deals you own end to end.',
    ],
    icon: 'stars',
    family: 'demand',
  },
]

/** One list of ad units covers all of it — the breadth claim the products are only worth anything against. */
export const INVENTORY_TYPES: { icon: IconName; label: string }[] = [
  { icon: 'desktop', label: 'Prebid.js' },
  { icon: 'build', label: 'Server-side' },
  { icon: 'mobile', label: 'In-app' },
  { icon: 'bolt', label: 'AMP' },
  // `video` rather than the `instream` illustration, which carries its own colours and would be the one
  // chip in the row not taking the text colour.
  { icon: 'video', label: 'Instream' },
]
